import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent, type MouseEvent } from 'react'
import { ApiError } from '../../api/client'
import { createFloor, deleteFloor, floorListQuery, updateFloor, type FloorList, type FloorSummary } from '../../api/floorPlans'
import { isForbidden } from '../../ui/Forbidden'
import { toast } from '../../ui/notify'

type Fields = { name: string; office_id: number | null; level_number: number | null }

/**
 * Cardul "Floor plans" din Settings -> Connection Quality: lista etajelor
 * firmei, grupata pe sediu, direct in coloana din dreapta - in locul unui
 * buton care ducea la `/app/settings/floor_plans`.
 *
 * Acelasi API ca pagina etajelor (`api/floorPlans.ts`, aceeasi cheie de
 * cache, deci o schimbare facuta aici se vede si acolo). Formularul e al lui
 * - cel din `floorplan/FloorPlansPage.tsx` nu e exportat. Pagina etajelor
 * ramane cum e; aici se creeaza, se redenumeste, se sterge si se deschide.
 */
export function FloorListCard() {
  const { data, error, refetch } = useQuery(floorListQuery)
  return (
    <section data-panel="floor-plans">
      <div className="od-cq-section">
        <h3>Floor plans</h3>
        <p>Each floor with its plan, access points and devices - to see where they are and how good the Wi-Fi is on it.</p>
      </div>
      <div className="od-da-card od-cq-floors">
        {error ? (
          isForbidden(error) ? (
            <p className="od-cq-floors-problem">You do not have access to floor plans.</p>
          ) : (
            <p className="od-cq-floors-problem" role="alert">
              The floors could not be loaded.{' '}
              <button type="button" className="btn btn-link btn-xs" onClick={() => void refetch()}>
                Try again
              </button>
            </p>
          )
        ) : !data ? (
          <p className="od-da-muted">Loading floors...</p>
        ) : (
          <Floors page={data} />
        )}
      </div>
    </section>
  )
}

function Floors({ page }: { page: FloorList }) {
  const query = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<number | null>(null)
  const byOffice = new Map<number | null, FloorSummary[]>()
  for (const f of page.floors) byOffice.set(f.office_id, [...(byOffice.get(f.office_id) ?? []), f])
  const officeName = (id: number | null) => (id === null ? 'No office' : (page.offices.find((o) => o.id === id)?.name ?? `Office ${id}`))
  const groups = [...byOffice.entries()].sort((a, b) => officeName(a[0]).localeCompare(officeName(b[0])))

  const remove = (f: FloorSummary) => {
    if (!window.confirm(`Delete "${f.name}"? Its plan, everything placed on it and all its surveys are deleted too.`)) return
    deleteFloor(f.id)
      .then((next) => {
        query.setQueryData(floorListQuery.queryKey, next)
        toast('Deleted.', 'Floor plans')
      })
      .catch((e: unknown) => toast(e instanceof ApiError && e.message ? e.message : 'It could not be deleted.', 'Floor plans', 'error'))
  }

  return (
    <>
      {page.floors.length === 0 && !adding ? (
        <div className="od-cq-empty" data-empty="floors">
          <strong>{page.canWrite ? 'No floors yet - add the first one' : 'No floors yet'}</strong>
          {page.canWrite
            ? 'Add a floor, open it, and upload its plan (PNG or JPG) and calibrate it - or draw it without a plan. Then place the access points and devices on it.'
            : 'Someone who can edit the company settings can add them.'}
        </div>
      ) : null}

      {page.floors.length > 0 ? <p className="od-cq-floors-hint">Click a floor to open its plan.</p> : null}

      {groups.map(([officeId, floors]) => (
        <div key={String(officeId)} className="od-cq-office" data-office={officeId ?? 'none'}>
          <h5>{officeName(officeId)}</h5>
          <ul className="od-cq-floor-list">
            {floors.map((f) =>
              editing === f.id ? (
                <li key={f.id} data-floor={f.id}>
                  <SmallFloorForm
                    page={page}
                    floor={f}
                    onCancel={() => setEditing(null)}
                    onSubmit={(fields) =>
                      updateFloor(f.id, fields).then((next) => {
                        query.setQueryData(floorListQuery.queryKey, next)
                        void query.invalidateQueries({ queryKey: ['settings', 'floor-plans', f.id] })
                        setEditing(null)
                      })
                    }
                  />
                </li>
              ) : (
                <li key={f.id} data-floor={f.id} className="od-cq-floor-row" onClick={openFloorRow}>
                  <a href={`/app/settings/floor_plans/${f.id}`} className="od-cq-floor-name" title="Open floor plan" data-floor-link>
                    <i className="fas fa-map-marked-alt od-cq-floor-icon" aria-hidden="true"></i>
                    <span>{f.name}</span>
                    <i className="fas fa-chevron-right od-cq-floor-arrow" aria-hidden="true"></i>
                  </a>
                  <span className="od-cq-floor-meta">
                    {f.level_number !== null ? `Level ${f.level_number}` : 'No level'}
                    {' · '}
                    {f.canvas && !f.has_image ? (
                      <span className="od-cq-floor-ok" data-plan="drawn">drawn (no plan)</span>
                    ) : !f.has_image ? (
                      <span className="od-cq-floor-noplan">no plan yet</span>
                    ) : f.scale_px_per_m === null ? (
                      <span className="text-warning">has plan, not calibrated</span>
                    ) : (
                      <span className="od-cq-floor-ok">has plan</span>
                    )}
                  </span>
                  {/* Un buton spus pe nume: numele etajului e un link, dar nu
                      se vedea ca pe el se apasa ca sa incarci planul (30 sep 2026). */}
                  <a
                    href={`/app/settings/floor_plans/${f.id}`}
                    className={`btn btn-sm od-cq-floor-go ${(!f.has_image && !f.canvas) || f.scale_px_per_m === null ? 'btn-primary' : 'btn-default'}`}
                    data-action="open-floor"
                  >
                    {!f.has_image && !f.canvas && page.canWrite ? (
                      <>
                        <i className="fas fa-pencil-ruler" aria-hidden="true"></i> Upload or draw
                      </>
                    ) : f.has_image && f.scale_px_per_m === null && page.canWrite ? (
                      <>
                        <i className="fas fa-ruler" aria-hidden="true"></i> Calibrate
                      </>
                    ) : (
                      <>
                        <i className="fas fa-map" aria-hidden="true"></i> Open plan
                      </>
                    )}
                  </a>
                  {page.canWrite ? (
                    <span className="od-cq-floor-actions">
                      <button type="button" className="btn btn-link btn-xs" onClick={() => setEditing(f.id)} aria-label={`Rename ${f.name}`} title="Rename">
                        <i className="fas fa-pen" aria-hidden="true"></i>
                      </button>
                      <button type="button" className="btn btn-link btn-xs text-danger" onClick={() => remove(f)} aria-label={`Delete ${f.name}`} title="Delete">
                        <i className="fas fa-trash" aria-hidden="true"></i>
                      </button>
                    </span>
                  ) : null}
                </li>
              ),
            )}
          </ul>
        </div>
      ))}

      {adding ? (
        <SmallFloorForm
          page={page}
          onCancel={() => setAdding(false)}
          onSubmit={(fields) =>
            createFloor(fields).then((next) => {
              query.setQueryData(floorListQuery.queryKey, next)
              setAdding(false)
              toast('Floor added. Open it to upload the plan.', 'Floor plans')
            })
          }
        />
      ) : null}

      <div className="od-cq-floors-foot">
        {page.canWrite && !adding ? (
          <button type="button" className="btn btn-success btn-sm" onClick={() => setAdding(true)} data-action="new-floor">
            <i className="fas fa-plus" aria-hidden="true"></i>New floor
          </button>
        ) : null}
        <a href="/app/settings/floor_plans" className="btn btn-link btn-sm" data-link="floor-plans">
          Manage floors <i className="fas fa-arrow-right" aria-hidden="true"></i>
        </a>
      </div>
    </>
  )
}

/**
 * Tot randul etajului deschide planul, nu doar numele (30 sep 2026: nu se
 * vedea ca pe nume se apasa). Un clic pe un buton, un link sau un camp din
 * rand isi face treaba lui; altfel se apasa linkul numelui, deci navigarea
 * ramane a lui.
 */
function openFloorRow(e: MouseEvent<HTMLElement>) {
  const target = e.target as HTMLElement
  if (target.closest('a, button, input, select, textarea, label, form')) return
  e.currentTarget.querySelector<HTMLAnchorElement>('a[data-floor-link]')?.click()
}

function SmallFloorForm({
  page,
  floor,
  onSubmit,
  onCancel,
}: {
  page: FloorList
  floor?: FloorSummary
  onSubmit(fields: Fields): Promise<void>
  onCancel(): void
}) {
  const [name, setName] = useState(floor?.name ?? '')
  const [office, setOffice] = useState(floor?.office_id != null ? String(floor.office_id) : '')
  const [level, setLevel] = useState(floor?.level_number != null ? String(floor.level_number) : '')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState('')

  const submit = (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setProblem('')
    onSubmit({ name: name.trim(), office_id: office === '' ? null : Number(office), level_number: level.trim() === '' ? null : Number(level) }).catch((err: unknown) => {
      setProblem(err instanceof ApiError && err.message ? err.message : 'It could not be saved.')
      setBusy(false)
    })
  }

  return (
    <form className="od-cq-floor-form" onSubmit={submit} data-form={floor ? 'rename-floor' : 'new-floor'}>
      <input className="form-control od-cq-floor-fname" aria-label="Floor name" placeholder="Floor name, e.g. Ground floor" maxLength={128} value={name} disabled={busy} onChange={(e) => setName(e.target.value)} />
      <select className="form-control" aria-label="Office" value={office} disabled={busy} onChange={(e) => setOffice(e.target.value)}>
        <option value="">No office</option>
        {page.offices.map((o) => (
          <option key={o.id} value={String(o.id)}>
            {o.name}
          </option>
        ))}
      </select>
      <input className="form-control od-cq-floor-level" aria-label="Level" placeholder="Level" inputMode="numeric" value={level} disabled={busy} onChange={(e) => setLevel(e.target.value)} />
      <button type="submit" className="btn btn-success btn-sm" disabled={busy || name.trim() === ''}>
        Save
      </button>
      <button type="button" className="btn btn-default btn-sm" disabled={busy} onClick={onCancel}>
        Cancel
      </button>
      {problem ? (
        <span className="text-danger" role="alert">
          {problem}
        </span>
      ) : null}
    </form>
  )
}
